/*
 * Round 14: the per-slot histories the server keeps, turned into what the Slot Stats page shows.
 *
 * WHAT THE SERVER STORES (IGAccStats, SlotServiceImpl ~4750 rolls them at the day change):
 *   <metric>DoneThisDaySticky   today's count so far
 *   <metric>DoneInPreviousDays  CSV, NEWEST FIRST, yesterday first, capped at 90 entries
 *                               ("0,0,3,0,2,...,1," - a trailing comma and ",," gaps occur)
 * for likes, comments, follows, unfollows (existing) and - round 14 - welcomeDms, storyViews,
 * storyLikes and postPostings.
 *
 * Growth (SlotServiceImpl ~3890-4035): dailyFollowersGrowthCsv / dailyFollowingsChangeCsv /
 * dailyPostsChangeCsv, NEWEST FIRST ("+5,-2,0,..."), index 0 = the day of the last daily profile
 * check (lastStatsCheckDateTime). lastChecked{Followers,Followings,Posts}Count are that check's
 * exact counts. Round 14b, checked against production 2026-09-29: lastStatsCheckDateTime is written
 * "yyyy-M-dd, HH:mm" ("2025-9-16, 23:30") in the SERVER's local time (IST: the newest check read
 * 08:10 when MySQL NOW() read 08:17 IST and UTC 02:47) - not "yyyy-MM-dd HH:mm" UTC as first noted.
 * Two more facts of that writer (round 14b review): it prepends ONE entry per check DATE, so a day
 * whose check was missed folds into the next entry (older dates then shift by a day); and the
 * oldest entry of an untruncated CSV is the first check's seed "0", not a measured change
 * (growthSeedAtEnd). The growth CSVs are not capped by the server; GROWTH_KEEP_DAYS are kept here.
 *
 * postsFollowersFolloweringsCountCsv is NOT a CSV (round-14 fix F3): it is the worker's raw
 * profile-header text, "posts followers following" joined by single SPACES, each token exactly as
 * Instagram shows it - "87 12,345 1,000", "1,234 12.3K 1.2M" (BC SR2 noteDownOurAccountDetails).
 * Splitting it on ',' read 12,345 as two numbers and put 0s on the tiles. The exact profile-check
 * counts are shown first; the worker's text is parsed as Instagram tokens only when those are
 * missing, and shown as raw text when it does not parse.
 *
 * Day anchors (round-14 fix F4): action index 0 is the day the slot's counters are on
 * (lastResetDateTimeDays, "2026-09-28T00:00", the slot's time zone), which is NOT the viewer's
 * today when the slot has not run since. Growth index 0 is the profile check's (server-time) date. Dates are
 * taken from those two fields (as V1's SlotStatsRound14 does), and the daily table pairs growth
 * and actions by DATE, not by index.
 *
 * Contract this page codes against: GET /uiapi/slots/{slotName}/stats answering those entity field
 * names, flat or under `stats`. Where a round-14 name is not certain yet, the alternatives are
 * listed in METRICS and the first one present wins.
 *
 * Nothing here turns "not sent" into 0 on screen: a metric the server did not send is
 * `present: false` and the page says "not recorded".
 */

export const HISTORY_DAYS = 90;

export const METRICS = [
  { key: 'likes', label: 'Likes', today: ['likesDoneThisDaySticky'], csv: ['likesDoneInPreviousDays'] },
  { key: 'comments', label: 'Comments', today: ['commentsDoneThisDaySticky'], csv: ['commentsDoneInPreviousDays'] },
  { key: 'follows', label: 'Follows', today: ['followsDoneThisDaySticky'], csv: ['followsDoneInPreviousDays'] },
  { key: 'unfollows', label: 'Unfollows', today: ['unfollowsDoneThisDaySticky'], csv: ['unfollowsDoneInPreviousDays'] },
  { key: 'welcomeDms', label: 'Welcome DMs', today: ['welcomeDmsDoneThisDaySticky'], csv: ['welcomeDmsDoneInPreviousDays'] },
  { key: 'storyViews', label: 'Story views', today: ['storyViewsDoneThisDaySticky'], csv: ['storyViewsDoneInPreviousDays'] },
  { key: 'storyLikes', label: 'Story likes', today: ['storyLikesDoneThisDaySticky'], csv: ['storyLikesDoneInPreviousDays'] },
  {
    key: 'posts', label: 'Posts',
    today: ['postPostingsDoneThisDaySticky', 'postsDoneThisDaySticky'],
    csv: ['postPostingsDoneInPreviousDays', 'postsDoneInPreviousDays'],
  },
];

export const GROWTH = [
  { key: 'followers', label: 'Followers', csv: ['dailyFollowersGrowthCsv'] },
  { key: 'followings', label: 'Following', csv: ['dailyFollowingsChangeCsv'] },
  { key: 'posts', label: 'Posts', csv: ['dailyPostsChangeCsv'] },
];

/**
 * How many growth entries are kept. The server does NOT cap the growth CSVs (SlotServiceImpl
 * ~3896-3930 only prepends), so twice the longest range is kept: the page compares a 90-day range
 * with the 90 days before it.
 */
export const GROWTH_KEEP_DAYS = 2 * HISTORY_DAYS;

/**
 * Round 14b: does the OLDEST growth entry hold the first profile check's seed rather than an
 * observed change? SlotServiceImpl writes the CSV as "0" when the slot has no previous count
 * (lastChecked*Count == null) and prepends one entry per later check date, so the oldest entry of
 * an untruncated CSV was never measured ("0", or "+c,0" after a same-day recheck). The posts CSV
 * written by the "posts posted externally" mode is different: ten per-day post counts, always
 * ending in a comma, with no seed. A CSV longer than we keep has lost its seed already.
 */
export function growthSeedAtEnd(csv) {
  if (csv === null || csv === undefined) return false;
  if (Array.isArray(csv)) return csv.length > 0 && csv.length <= GROWTH_KEEP_DAYS;
  const s = String(csv).trim().replace(/^\[/, '').replace(/\]$/, '').trim();
  if (s === '' || s.endsWith(',')) return false;
  const n = parseHistoryCsv(s).length;
  return n > 0 && n <= GROWTH_KEEP_DAYS;
}

// The actions a follower can come from. Story views are left out on purpose: a view is not an
// action the other account is notified of.
const ACTION_KEYS = ['likes', 'comments', 'follows', 'welcomeDms', 'storyLikes'];

const toNumber = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (s === '' || s.toLowerCase() === 'null') return null;
  const n = Number(s.replace(/^\+/, ''));
  return Number.isFinite(n) ? n : null;
};

/**
 * "0,+3,-2,,null,5," -> [0, 3, -2, 0, 0, 5]. Newest first, as stored. Brackets from a
 * List.toString() are tolerated. A trailing comma is dropped; a gap inside the list is a 0,
 * which is how the server's own reader (getEngDoneInStartingXDays) treats it.
 */
export function parseHistoryCsv(csv) {
  if (csv === null || csv === undefined) return [];
  if (Array.isArray(csv)) return csv.map((v) => toNumber(v) ?? 0);
  let s = String(csv).trim().replace(/^\[/, '').replace(/\]$/, '');
  if (s === '') return [];
  const parts = s.split(',');
  while (parts.length && parts[parts.length - 1].trim() === '') parts.pop();
  return parts.map((p) => toNumber(p) ?? 0);
}

const pick = (src, names) => {
  for (let i = 0; i < names.length; i++) {
    if (src && Object.prototype.hasOwnProperty.call(src, names[i]) && src[names[i]] !== undefined) {
      return { found: true, value: src[names[i]] };
    }
  }
  return { found: false, value: undefined };
};

/** [today, yesterday, ...] capped at `days`. `today` null/absent counts as 0. */
export function dailySeries(today, previousCsv, days = HISTORY_DAYS) {
  const t = toNumber(today);
  return [t === null ? 0 : t].concat(parseHistoryCsv(previousCsv)).slice(0, days);
}

/** Sum of series[from .. from+len-1]; days beyond the recorded history count as nothing. */
export function windowSum(series, from, len) {
  let sum = 0;
  for (let i = from; i < from + len && i < series.length; i++) sum += series[i] || 0;
  return sum;
}

/** Daily / weekly / monthly totals: today, the last 7 days, the last 30 days, all recorded. */
export function periodTotals(series) {
  return {
    today: series.length ? series[0] : 0,
    week: windowSum(series, 0, 7),
    month: windowSum(series, 0, 30),
    all: windowSum(series, 0, series.length),
    days: series.length,
  };
}

/**
 * Consecutive windows, newest first: bucketTotals(s, 7, 4) = the last four weeks.
 * A window with no recorded day at all is left out rather than shown as 0.
 */
export function bucketTotals(series, size, count) {
  const out = [];
  for (let b = 0; b < count; b++) {
    const from = b * size;
    if (from >= series.length) break;
    out.push({ index: b, sum: windowSum(series, from, size), days: Math.min(size, series.length - from) });
  }
  return out;
}

/** The calendar day for index i (0 = today) as YYYY-MM-DD, in the viewer's local time. */
export function dayLabel(i, now = new Date()) {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * The YYYY-MM-DD day of one of the server's date fields, or null when it is not a date.
 * Accepts "2026-09-28T00:00" (LocalDateTime.toString), "2026-09-28 10:15", "2026-09-28", Jackson's
 * array form [2026, 9, 28, 0, 0], and - round 14b - the stats check's REAL format "2026-9-28, 04:10"
 * (SlotServiceImpl writes lastStatsCheckDateTime with the pattern "yyyy-M-dd, HH:mm": a one-digit
 * month and a comma). Before 14b that format read as "no date" and every growth day was dated
 * from the action counters' day instead of the profile check's.
 */
export function isoDay(v) {
  if (v === null || v === undefined) return null;
  let y; let m; let d;
  if (Array.isArray(v)) {
    [y, m, d] = v.map(Number);
  } else {
    const hit = String(v).trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:$|[T ,])/);
    if (!hit) return null;
    [y, m, d] = [Number(hit[1]), Number(hit[2]), Number(hit[3])];
  }
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  const t = new Date(Date.UTC(y, m - 1, d));
  if (t.getUTCFullYear() !== y || t.getUTCMonth() !== m - 1 || t.getUTCDate() !== d) return null;
  return t.toISOString().slice(0, 10);
}

/** YYYY-MM-DD minus n calendar days (UTC arithmetic: no daylight-saving drift). */
export function shiftDay(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d - n)).toISOString().slice(0, 10);
}

/** Whole calendar days from `later` back to `earlier` (later - earlier). */
export function daysBetween(later, earlier) {
  const ms = (iso) => { const [y, m, d] = iso.split('-').map(Number); return Date.UTC(y, m - 1, d); };
  return Math.round((ms(later) - ms(earlier)) / 86400000);
}

/**
 * One Instagram profile-header count exactly as the worker read it off the screen:
 * "87", "12,345", "1.234" (dot-grouped thousands), "12.3K", "1.2M", "12,3K" (decimal comma),
 * "1B". Returns { value, exact, text }: `exact` is false for a K/M/B figure, which Instagram
 * rounds. Anything else (another language's words, an empty token) is null - never a 0.
 */
export function parseIgCount(token) {
  if (token === null || token === undefined) return null;
  const t = String(token).trim();
  if (t === '') return null;
  if (/^\d+$/.test(t)) return { value: Number(t), exact: true, text: t };
  if (/^\d{1,3}(,\d{3})+$/.test(t) || /^\d{1,3}(\.\d{3})+$/.test(t)) {
    return { value: Number(t.replace(/[.,]/g, '')), exact: true, text: t };
  }
  const m = t.match(/^(\d+(?:[.,]\d{1,2})?)\s?([KkMmBb])$/);
  if (m) {
    const mult = { k: 1e3, m: 1e6, b: 1e9 }[m[2].toLowerCase()];
    return { value: Math.round(Number(m[1].replace(',', '.')) * mult), exact: false, text: t.replace(/\s/g, '') };
  }
  return null;
}

/**
 * The worker's "posts followers following" header text -> { posts, followers, followings } of
 * parseIgCount results, or null when it is not exactly three counts. A lone K/M/B token is
 * joined to the number before it ("12.3 K").
 */
export function parseCountsSnapshot(text) {
  if (text === null || text === undefined) return null;
  const raw = String(text).trim();
  if (raw === '') return null;
  const tokens = [];
  raw.split(/\s+/).forEach((tok) => {
    if (/^[KkMmBb]$/.test(tok) && tokens.length) tokens[tokens.length - 1] += tok;
    else tokens.push(tok);
  });
  if (tokens.length !== 3) return null;
  const [posts, followers, followings] = tokens.map(parseIgCount);
  if (!posts || !followers || !followings) return null;
  return { posts, followers, followings };
}

const ratio = (num, den) => (den > 0 ? Math.round((num / den) * 1000) / 10 : null);

/** Sum of series[from .. from+len-1] where `from` may be negative (days after the series' day 0 count as nothing). */
const windowSumAt = (series, from, len) => {
  let sum = 0;
  for (let i = Math.max(0, from); i < from + len && i < series.length; i++) sum += series[i] || 0;
  return sum;
};

/**
 * Everything the Slot Stats page draws, from the server's answer.
 * @param raw  the GET /uiapi/slots/{slot}/stats body
 * @param now  the viewer's clock (tests pass a fixed one)
 * @returns {{
 *   metrics: Array<{key,label,present,series:number[],totals}>,
 *   growth:  Array<{key,label,present,series:number[],seed:boolean,weeks,months}>,
 *                                  seed: the oldest entry is the first check's seed, not a change
 *   current: {source:'profileCheck'|'worker', posts, followers, followings, text:{posts,followers,followings},
 *             approximate:boolean, approx:{posts,followers,followings}}|null,
 *   countsSnapshot: string|null,   the worker's raw "posts followers following" header text
 *   today: string,                 the viewer's YYYY-MM-DD
 *   actionsDay0: string,           the day action index 0 belongs to (lastResetDateTimeDays, else today)
 *   actionsDay0Known: boolean,     false = the server did not send lastResetDateTimeDays
 *   actionsDay0Suspect: boolean,   lastResetDateTimeDays is not at 00:00 (a UTC value leaked; warned)
 *   growthDay0: string|null,       the day growth index 0 belongs to (the stats check's date, server time)
 *   lastStatsCheck: string|null,   lastStatsCheckDateTime as sent ("yyyy-M-dd, HH:mm", server time)
 *   ratios:  Array<{key,label,week,month}>,
 *   anyPresent: boolean,
 * }}
 */
/**
 * Round 14b: the time part of lastResetDateTimeDays, "HH:mm", or null when it has none. The server
 * writes the slot's midnight (dateNow.atStartOfDay()) and /uiapi sends it back after Hibernate's
 * UTC -> IST conversion, so anything but "00:00" means a raw UTC value leaked through (a DB export
 * reads "2025-09-07T18:30" for the API's "2025-09-08T00:00") and every action day is one day early.
 */
export function resetTimeOf(v) {
  if (v === null || v === undefined) return null;
  if (Array.isArray(v)) {
    if (v.length < 4) return null;
    return `${String(Number(v[3]) || 0).padStart(2, '0')}:${String(Number(v[4]) || 0).padStart(2, '0')}`;
  }
  const m = String(v).trim().match(/^\d{4}-\d{1,2}-\d{1,2}[T ](\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : null;
}

const warned = new Set();
const warnOnce = (key, msg) => {
  if (warned.has(key)) return;
  warned.add(key);
  // eslint-disable-next-line no-console
  console.warn(msg);
};

export function normaliseSlotStats(raw, now = new Date()) {
  const src = (raw && (raw.stats || raw.igAccStats)) || raw || {};
  const today = dayLabel(0, now);
  const resetDay = isoDay(src.lastResetDateTimeDays);
  const resetTime = resetTimeOf(src.lastResetDateTimeDays);
  const actionsDay0Suspect = resetDay !== null && resetTime !== null && resetTime !== '00:00';
  if (actionsDay0Suspect) {
    warnOnce(`reset:${src.lastResetDateTimeDays}`, `[slotStats] lastResetDateTimeDays=${src.lastResetDateTimeDays} is not at `
      + 'midnight: the server sends the slot\'s 00:00, so this looks like a raw UTC value and every action day may be one day early');
  }
  const actionsDay0 = resetDay || today;
  const lastStatsCheck = typeof src.lastStatsCheckDateTime === 'string' && src.lastStatsCheckDateTime.trim()
    ? src.lastStatsCheckDateTime.trim() : null;
  const growthDay0 = isoDay(lastStatsCheck);

  const metrics = METRICS.map((m) => {
    const t = pick(src, m.today);
    const c = pick(src, m.csv);
    // A key sent as JSON null is "not recorded", not 0 (round-14 note N8): the metric is present
    // only when today's counter is a number or the stored history has at least one day.
    const todayKnown = t.found && toNumber(t.value) !== null;
    const historyKnown = c.found && parseHistoryCsv(c.value).length > 0;
    const present = todayKnown || historyKnown;
    const series = present ? dailySeries(t.value, c.value) : [];
    // `sent`: the server knows the field (a null value = nothing recorded yet), as opposed to a
    // server that does not send it at all.
    return { key: m.key, label: m.label, present, sent: t.found || c.found, series, totals: periodTotals(series) };
  });

  const growth = GROWTH.map((g) => {
    const c = pick(src, g.csv);
    const series = c.found ? parseHistoryCsv(c.value).slice(0, GROWTH_KEEP_DAYS) : [];
    return {
      key: g.key, label: g.label, present: c.found && series.length > 0, series,
      // the oldest entry is the first check's seed, not a measured change (round 14b)
      seed: c.found && growthSeedAtEnd(c.value),
      weeks: bucketTotals(series, 7, 4),
      months: bucketTotals(series, 30, 3),
    };
  });

  // The current counts. First the daily profile check's exact numbers; else the worker's header
  // text read as Instagram tokens (F3: it is space-separated, "12,345" is ONE number).
  const snap = pick(src, ['postsFollowersFolloweringsCountCsv']);
  const countsSnapshot = snap.found && snap.value !== null && String(snap.value).trim() !== ''
    ? String(snap.value).trim() : null;
  let current = null;
  const f = toNumber(src.lastCheckedFollowersCount);
  const g = toNumber(src.lastCheckedFollowingsCount);
  const p = toNumber(src.lastCheckedPostsCount);
  if (f !== null || g !== null || p !== null) {
    const txt = (n) => (n === null ? null : n.toLocaleString());
    current = {
      source: 'profileCheck', posts: p, followers: f, followings: g,
      text: { posts: txt(p), followers: txt(f), followings: txt(g) }, approximate: false,
      approx: { posts: false, followers: false, followings: false },
    };
  } else {
    const parsed = parseCountsSnapshot(countsSnapshot);
    if (parsed) {
      const txt = (c) => (c.exact ? c.value.toLocaleString() : c.text);
      current = {
        source: 'worker',
        posts: parsed.posts.value, followers: parsed.followers.value, followings: parsed.followings.value,
        text: { posts: txt(parsed.posts), followers: txt(parsed.followers), followings: txt(parsed.followings) },
        approximate: !(parsed.posts.exact && parsed.followers.exact && parsed.followings.exact),
        // round 14b: per count - "12.3K" is rounded, "980" next to it is not
        approx: { posts: !parsed.posts.exact, followers: !parsed.followers.exact, followings: !parsed.followings.exact },
      };
    }
  }

  // Actions-done ratios. NO engagement rate: that needs extra phone activity (owner, 2026-09-28).
  // The windows are paired by DATE (F4): growth counts back from growthDay0, actions from
  // actionsDay0, so the action window starts `offset` entries into the action series.
  const byKey = {};
  metrics.forEach((m) => { byKey[m.key] = m; });
  const followers = growth[0];
  const offset = growthDay0 ? daysBetween(actionsDay0, growthDay0) : 0;
  const ratios = [];
  if (followers.present) {
    const gained = (len) => windowSum(followers.series, 0, len);
    const actions = (len) => ACTION_KEYS.reduce((n, k) => n + (byKey[k].present ? windowSumAt(byKey[k].series, offset, len) : 0), 0);
    if (byKey.follows.present) {
      ratios.push({
        key: 'perFollow', label: 'Followers gained per 100 follows',
        week: ratio(gained(7), windowSumAt(byKey.follows.series, offset, 7)),
        month: ratio(gained(30), windowSumAt(byKey.follows.series, offset, 30)),
      });
    }
    if (ACTION_KEYS.some((k) => byKey[k].present)) {
      ratios.push({
        key: 'perAction', label: 'Followers gained per 100 actions (likes, comments, follows, welcome DMs, story likes)',
        week: ratio(gained(7), actions(7)),
        month: ratio(gained(30), actions(30)),
      });
    }
  }

  return {
    metrics, growth, current, countsSnapshot, ratios,
    today, actionsDay0, actionsDay0Known: resetDay !== null, actionsDay0Suspect, growthDay0, lastStatsCheck,
    anyPresent: metrics.some((m) => m.present) || growth.some((gr) => gr.present) || current !== null
      || countsSnapshot !== null,
  };
}

/**
 * The daily table (newest first, at most `maxRows` rows): growth and actions paired by DATE
 * (F4), not by index. Growth dates count back from growthDay0 (falling back to actionsDay0, as
 * V1 does), action dates from actionsDay0. A value outside its recorded range is null ("-").
 * @returns Array<{date, isToday, followers, followings, posts, actions}>
 */
export function dailyTableRows(stats, maxRows = 14) {
  const [followers, followings, posts] = stats.growth;
  const aDay0 = stats.actionsDay0;
  const gDay0 = stats.growthDay0 || aDay0;
  const actionMetrics = stats.metrics.filter((m) => m.present && ACTION_KEYS.includes(m.key));
  const anyGrowth = stats.growth.some((g) => g.present);
  const anchors = [];
  if (anyGrowth) anchors.push(gDay0);
  if (actionMetrics.length) anchors.push(aDay0);
  if (!anchors.length) return [];
  const newest = anchors.reduce((a, b) => (daysBetween(a, b) >= 0 ? a : b));
  const at = (series, i) => (i >= 0 && i < series.length ? series[i] : null);
  // A calendar window of maxRows days from the newer anchor. A day inside it with nothing known
  // stays as a row of dashes (a gap is information); only the trailing empty days are dropped.
  const rows = [];
  for (let k = 0; k < maxRows; k++) {
    const date = shiftDay(newest, k);
    const gi = daysBetween(gDay0, date);
    const ai = daysBetween(aDay0, date);
    const inActions = actionMetrics.some((m) => ai >= 0 && ai < m.series.length);
    const row = {
      date,
      isToday: date === stats.today,
      followers: at(followers.series, gi),
      followings: at(followings.series, gi),
      posts: at(posts.series, gi),
      actions: inActions ? actionMetrics.reduce((n, m) => n + (at(m.series, ai) || 0), 0) : null,
    };
    rows.push(row);
  }
  const empty = (r) => r.followers === null && r.followings === null && r.posts === null && r.actions === null;
  while (rows.length && empty(rows[rows.length - 1])) rows.pop();
  return rows;
}
